/**
 * Business logic for grouping transactions into recurring patterns
 * and calculating recurring transaction summaries
 */

import type { Transaction } from '../types';
import type { RecurringTransaction, RecurringSummary, RecurringStatus, RecurringType } from '../../types/recurring';

/**
 * Group recurring transactions by pattern and calculate metadata
 * Transforms raw transactions marked as recurring into structured recurring groups
 *
 * @param allTransactions - All transactions from storage
 * @returns Array of recurring transaction groups with calculated metadata
 */
export function groupRecurringTransactions(
  allTransactions: Transaction[]
): RecurringTransaction[] {
  // Filter transactions marked as recurring
  const recurringTransactions = allTransactions.filter(t => t.isRecurring);

  // Group transactions by pattern (displayName)
  const grouped = new Map<string, Transaction[]>();
  for (const t of recurringTransactions) {
    const pattern = t.displayName || t.description;

    if (!grouped.has(pattern)) {
      grouped.set(pattern, []);
    }
    grouped.get(pattern)!.push(t);
  }

  // Convert grouped transactions to RecurringTransaction format
  const recurring = Array.from(grouped.entries()).map(([pattern, transactions]) => {
    return buildRecurringTransaction(pattern, transactions);
  });

  return recurring;
}

/**
 * Build a RecurringTransaction from a group of transactions
 * Calculates all metadata including amounts, intervals, and status
 *
 * @param pattern - The pattern/display name for this group
 * @param transactions - Array of transactions in this group
 * @returns A fully populated RecurringTransaction object
 */
function buildRecurringTransaction(
  pattern: string,
  transactions: Transaction[]
): RecurringTransaction {
  // Sort by date to get most recent and oldest
  const sorted = transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const latest = sorted[0];
  const oldest = sorted[sorted.length - 1];

  // Calculate amounts
  const amounts = transactions.map(t => Math.abs(t.amount));
  const totalAmount = amounts.reduce((sum, a) => sum + a, 0);
  const avgAmount = totalAmount / transactions.length;
  const minAmount = Math.min(...amounts);
  const maxAmount = Math.max(...amounts);

  // Calculate average interval (rounded to nearest day)
  const avgInterval = calculateAverageInterval(sorted);

  // Calculate next expected date and status
  const { expectedDate, daysUntilDue, status } = calculateRecurringStatus(
    latest.date,
    avgInterval
  );

  return {
    pattern: pattern,
    displayName: pattern,
    category: latest.category,
    avgAmount: avgAmount,
    minAmount: minAmount,
    maxAmount: maxAmount,
    avgInterval: avgInterval,
    occurrences: transactions.length,
    firstDate: oldest.date,
    lastDate: latest.date,
    expectedDate: expectedDate,
    daysUntilDue: daysUntilDue,
    recurringType: (latest.recurringType || 'subscription') as RecurringType,
    confidence: 1.0,
    status: status,
    totalPaid: totalAmount,
    averageAmount: avgAmount,
    transactionCount: transactions.length
  };
}

/**
 * Calculate the average interval between transactions
 *
 * @param sortedTransactions - Transactions sorted by date (newest first)
 * @returns Average interval in days (default 30 if only 1 transaction)
 */
function calculateAverageInterval(sortedTransactions: Transaction[]): number {
  let avgInterval = 30; // Default to monthly

  if (sortedTransactions.length > 1) {
    const intervals: number[] = [];
    for (let i = 0; i < sortedTransactions.length - 1; i++) {
      const days = (new Date(sortedTransactions[i].date).getTime() -
                   new Date(sortedTransactions[i + 1].date).getTime()) /
                   (1000 * 60 * 60 * 24);
      intervals.push(days);
    }
    avgInterval = Math.round(intervals.reduce((sum, i) => sum + i, 0) / intervals.length);
  }

  return avgInterval;
}

/**
 * Calculate the status and timing information for a recurring transaction
 *
 * @param lastDate - Date of the last transaction occurrence
 * @param avgInterval - Average interval in days between transactions
 * @returns Object with expected date, days until due, and status
 */
export function calculateRecurringStatus(
  lastDate: string,
  avgInterval: number
): { expectedDate: string; daysUntilDue: number; status: RecurringStatus } {
  // Calculate next expected date
  const lastDateTime = new Date(lastDate);
  const expectedDate = new Date(lastDateTime);
  expectedDate.setDate(expectedDate.getDate() + avgInterval);

  // Calculate days until due
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
  const expectedDateTime = new Date(expectedDate);
  expectedDateTime.setHours(0, 0, 0, 0);

  const daysUntilDue = Math.round(
    (expectedDateTime.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Determine status based on days until due
  let status: RecurringStatus;
  if (daysUntilDue < -avgInterval) {
    // Past expected date by more than one cycle - possibly ended
    status = 'possibly_ended';
  } else if (daysUntilDue < 0) {
    // Past due date
    status = 'overdue';
  } else if (daysUntilDue === 0) {
    // Due today
    status = 'due_today';
  } else if (daysUntilDue <= 7) {
    // Due within a week
    status = 'due_soon';
  } else {
    // Not due yet
    status = 'not_due';
  }

  return {
    expectedDate: expectedDate.toISOString().split('T')[0],
    daysUntilDue,
    status
  };
}

/**
 * Calculate summary statistics for recurring transactions
 *
 * @param recurring - Array of recurring transactions
 * @returns Summary with total monthly amount and breakdowns by type
 */
export function calculateRecurringSummary(
  recurring: RecurringTransaction[]
): RecurringSummary {
  const subscriptions = recurring.filter(r => r.recurringType === 'subscription');
  const bills = recurring.filter(r => r.recurringType === 'bill');

  return {
    totalMonthly: recurring.reduce((sum, r) => sum + r.avgAmount, 0),
    subscriptions: {
      count: subscriptions.length,
      total: subscriptions.reduce((sum, r) => sum + r.avgAmount, 0)
    },
    bills: {
      count: bills.length,
      total: bills.reduce((sum, r) => sum + r.avgAmount, 0)
    }
  };
}

/**
 * Get status badge CSS class for a given status
 *
 * @param status - The recurring transaction status
 * @returns CSS class name for styling the status badge
 */
export function getStatusBadgeClass(status: RecurringStatus): string {
  switch (status) {
    case 'due_today': return 'status-due-today';
    case 'due_soon': return 'status-due-soon';
    case 'overdue': return 'status-overdue';
    case 'possibly_ended': return 'status-possibly-ended';
    default: return 'status-not-due';
  }
}

/**
 * Get human-readable status label for display
 *
 * @param daysUntilDue - Days until next expected payment (negative = overdue)
 * @param status - The recurring transaction status
 * @returns Human-readable status label
 */
export function getStatusLabel(daysUntilDue: number, status: RecurringStatus): string {
  if (status === 'due_today') return 'Due Today';
  if (status === 'possibly_ended') return 'Possibly Cancelled';
  if (daysUntilDue > 0) return `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}`;
  if (status === 'overdue') return `Overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? 's' : ''}`;
  return 'Not due';
}
